import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { SignupStep } from './steps/SignupStep.tsx';
import { PaymentStep } from './steps/PaymentStep.tsx';
import { ConfirmationStep } from './steps/ConfirmationStep.tsx';
import { createPaymentIntent, registerBusinessUser } from '../../services/api.ts';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
`;

const Step = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  align-items: center;
  margin: 0 1rem;
  color: ${(props) => (props.active ? '#4299e1' : props.completed ? '#48bb78' : '#a0aec0')};
  font-weight: ${(props) => (props.active ? '600' : '400')};
`;

const StepNumber = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.active ? '#4299e1' : props.completed ? '#48bb78' : '#e2e8f0'};
  color: white;
  margin-right: 0.5rem;
`;

const StepTitle = styled.span``;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 2rem;
  color: #2d3748;
`;

const ErrorMessage = styled.div`
  background-color: #fed7d7;
  color: #c53030;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  text-align: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
`;

const LoadingText = styled.p`
  font-size: 1.1rem;
  color: #4a5568;
  margin-top: 1rem;
`;

interface PurchasePageProps {}

export const PurchasePage: React.FC<PurchasePageProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registeredUser, setRegisteredUser] = useState<any>(null);
  
  // Get plan data from location state or redirect to pricing
  const plan = location.state?.plan;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    phoneNumber: '',
  });

  useEffect(() => {
    if (!plan) {
      navigate('/pricing');
    }
  }, [plan, navigate]);

  useEffect(() => {
    // Create payment intent when user reaches payment step
    if (currentStep === 2 && !clientSecret) {
      const createIntent = async () => {
        try {
          setLoading(true);
          const response = await createPaymentIntent({
            planId: plan.id,
            currency: 'usd'
          });
          setClientSecret(response.clientSecret);
          setLoading(false);
        } catch (err) {
          setError('Failed to initialize payment. Please try again.');
          setLoading(false);
        }
      };
      
      createIntent();
    }
  }, [currentStep, clientSecret, plan]);

  const updateFormData = (data: Partial<typeof formData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      // Register user before proceeding to payment
      try {
        setLoading(true);
        setError(null);
        
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          companyName: formData.companyName,
          phoneNumber: formData.phoneNumber,
          planId: plan.id
        };
        
        const response = await registerBusinessUser(userData);
        // Store the registered user data
        setRegisteredUser(response);
        setCurrentStep(currentStep + 1);
        setLoading(false);
      } catch (err: any) {
        setError(err.message || 'Failed to create account. Please try again.');
        setLoading(false);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePaymentSuccess = () => {
    setCurrentStep(3);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <SignupStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            loading={loading}
          />
        );
      case 2:
        if (loading || !clientSecret) {
          return (
            <LoadingContainer>
              <div className="spinner"></div>
              <LoadingText>Preparing payment form...</LoadingText>
            </LoadingContainer>
          );
        }
        return (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentStep
              plan={plan}
              onSuccess={handlePaymentSuccess}
            />
          </Elements>
        );
      case 3:
        return (
          <ConfirmationStep
            plan={plan}
            formData={formData}
            user={registeredUser}
          />
        );
      default:
        return null;
    }
  };

  if (!plan) {
    return null;
  }

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1:
        return 'Account';
      case 2:
        return 'Payment';
      case 3:
        return 'Confirmation';
      default:
        return '';
    }
  };

  return (
    <Container>
      <Helmet>
        <title>Complete Your Purchase - Spin To Connect</title>
        <meta name="description" content="Securely complete your plan purchase for Spin To Connect and start revolutionizing your customer engagement." />
        {/* Consider adding Open Graph tags specific to the purchase process */}
      </Helmet>
      <Title>Complete Your Purchase</Title>
      
      <StepsContainer>
        {[1, 2, 3].map((step) => (
          <Step
            key={step}
            active={step === currentStep}
            completed={step < currentStep}
          >
            <StepNumber
              active={step === currentStep}
              completed={step < currentStep}
            >
              {step}
            </StepNumber>
            <StepTitle>{getStepTitle(step)}</StepTitle>
          </Step>
        ))}
      </StepsContainer>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {renderStep()}
    </Container>
  );
}; 